import { useCallback } from 'react';

/**
 * Wraps an `onComplete` callback so that if it throws, a cleanup routine
 * (e.g. deleting the secret/account that was just created) runs before the
 * error is rethrown. `shouldDeleteOnFailure` lets the consumer opt out of
 * cleanup for a given id; when omitted, cleanup always runs.
 *
 * `onError`, if provided, runs before cleanup — useful for telemetry that
 * needs the caught error (e.g. `captureException`).
 *
 * Shared by the account and auth modals, which otherwise each reimplement
 * the same try/cleanup/rethrow skeleton around `onComplete`.
 */
export function useCompleteWithCleanup(onComplete, shouldDeleteOnFailure, cleanup, onError) {
  return useCallback(id => {
    try {
      onComplete(id);
    } catch (error) {
      try {
        onError === null || onError === void 0 || onError(error, id);
      } catch (_unused) {
        // Suppress errors from onError so cleanup always runs and the
        // original error is rethrown unchanged.
      }
      const shouldCleanup = shouldDeleteOnFailure ? shouldDeleteOnFailure(id) : true;
      if (shouldCleanup) {
        cleanup(id);
      }
      throw error;
    }
  }, [onComplete, shouldDeleteOnFailure, cleanup, onError]);
}